import { Fragment } from 'react';
import type { Paleta } from '@miolos/core';
import { MESES_ABREV, matrizDoMes, nomeMes } from '@miolos/core';

interface Props {
  ano: number;
  mes: number;
  destacar?: number[];
  mostrarNumeroSemana?: boolean;
  rotulo?: string;
  semDatas?: boolean;
  /** Feriados do mês, já resolvidos pelo motor. */
  feriados?: Array<{ dia: number; nome: string; tipo: 'NACIONAL' | 'ESTADUAL'; facultativo: boolean }>;
  /** Lista o nome dos feriados abaixo da grade. */
  mostrarNomesFeriados?: boolean;
  paleta: Paleta;
  rotuloCor: string;
}

const INICIAIS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/**
 * Mini calendário do mês.
 *
 * Os tamanhos de fonte usam container queries (`cqw`), então a mesma página
 * funciona em A4, A5 e A6 sem recalcular nada — o calendário se adapta à
 * largura que receber.
 */
export function MiniCalendario({
  ano,
  mes,
  destacar,
  mostrarNumeroSemana,
  rotulo,
  semDatas,
  feriados,
  mostrarNomesFeriados,
  paleta,
  rotuloCor,
}: Props) {
  const semanas = matrizDoMes(ano, mes);
  const destacados = new Set(destacar ?? []);
  const colunas = mostrarNumeroSemana ? 8 : 7;
  /** Feriado pelo dia do mês, para marcar a célula e listar o nome no rodapé. */
  const feriadosPorDia = new Map((feriados ?? []).map((feriado) => [feriado.dia, feriado]));
  const nomesDeFeriado = mostrarNomesFeriados === true && !semDatas ? (feriados ?? []) : [];

  const numeroSemana = (iso: string | null) => {
    if (!iso) return '';
    const data = new Date(`${iso}T00:00:00Z`);
    const diaSemana = (data.getUTCDay() + 6) % 7;
    data.setUTCDate(data.getUTCDate() - diaSemana + 3);
    const primeiraQuinta = new Date(Date.UTC(data.getUTCFullYear(), 0, 4));
    return String(1 + Math.round((data.getTime() - primeiraQuinta.getTime()) / (7 * 86400000)));
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8mm',
        flex: '1 1 auto',
        minHeight: 0,
        minWidth: 0,
        containerType: 'inline-size',
      }}
    >
      {rotulo ? (
        <span
          style={{
            fontSize: '8.6cqw',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: rotuloCor,
            lineHeight: 1.1,
          }}
        >
          {rotulo}
        </span>
      ) : (
        <span
          style={{
            fontSize: '8cqw',
            fontWeight: 600,
            color: rotuloCor,
            lineHeight: 1.1,
            textTransform: 'capitalize',
          }}
        >
          {nomeMes(mes)}
          {ano ? ` ${ano}` : ''}
        </span>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
          /**
           * As semanas dividem a altura que o calendário recebeu: se a célula da
           * grade vier mais baixa que o desenho (papel pequeno, muitos meses),
           * elas encolhem juntas em vez de a última linha sair cortada pela
           * margem da folha. Numa altura indefinida o `fr` vale o conteúdo, então
           * o desenho continua do tamanho natural quando há espaço de sobra.
           */
          gridAutoRows: 'minmax(0, 1fr)',
          gap: '0.4mm',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {INICIAIS.map((inicial, i) => (
          <span
            key={`cab-${i}`}
            style={{
              fontSize: '6.4cqw',
              textAlign: 'center',
              fontWeight: 700,
              color: paleta.textoSuave,
              textTransform: 'uppercase',
              lineHeight: 1.4,
            }}
          >
            {inicial}
          </span>
        ))}
        {mostrarNumeroSemana ? (
          <span style={{ fontSize: '5.6cqw', textAlign: 'center', color: paleta.textoSuave, fontWeight: 700 }}>S</span>
        ) : null}

        {semanas.map((semana, indiceSemana) =>
          semDatas ? (
            <Fragment key={`vazia-${indiceSemana}`}>
              {Array.from({ length: 7 }).map((__, i) => (
                <span
                  key={`v-${indiceSemana}-${i}`}
                  style={{
                    border: `0.15mm solid ${paleta.borda}`,
                    borderRadius: '0.5mm',
                    minHeight: '5.5cqw',
                  }}
                />
              ))}
              {mostrarNumeroSemana ? (
                <span key={`vn-${indiceSemana}`} style={{ fontSize: '5.6cqw', textAlign: 'center', color: paleta.textoSuave }}>
                  {indiceSemana + 1}
                </span>
              ) : null}
            </Fragment>
          ) : (
            <Fragment key={`semana-${indiceSemana}`}>
              {semana.map((iso, i) => {
                if (!iso) {
                  return <span key={`v-${indiceSemana}-${i}`} style={{ minHeight: '5.5cqw' }} />;
                }
                const dia = Number.parseInt(iso.slice(-2), 10);
                const marcado = destacados.has(dia);
                const feriado = feriadosPorDia.get(dia);
                return (
                  <span
                    key={iso}
                    style={{
                      fontSize: '7.6cqw',
                      textAlign: 'center',
                      lineHeight: 1.35,
                      fontWeight: marcado || feriado ? 800 : 400,
                      color: marcado
                        ? paleta.destaqueTexto
                        : feriado
                          ? feriado.facultativo
                            ? paleta.textoSuave
                            : paleta.principal
                          : paleta.texto,
                      background: marcado ? paleta.destaqueSuave : 'transparent',
                      borderRadius: '0.6mm',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '5.5cqw',
                    }}
                  >
                    {dia}
                    {feriado ? (
                      <span
                        aria-hidden
                        style={{
                          display: 'block',
                          width: '1.5cqw',
                          height: '1.5cqw',
                          borderRadius: '50%',
                          marginTop: '0.2mm',
                          background: feriado.facultativo ? paleta.borda : paleta.principal,
                        }}
                      />
                    ) : null}
                  </span>
                );
              })}
              {mostrarNumeroSemana ? (
                <span
                  key={`n-${indiceSemana}`}
                  style={{ fontSize: '5.6cqw', textAlign: 'center', color: paleta.textoSuave, fontWeight: 600 }}
                >
                  {numeroSemana(semana.find((iso) => iso) ?? null)}
                </span>
              ) : null}
            </Fragment>
          ),
        )}
      </div>

      {nomesDeFeriado.length > 0 ? (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.2mm',
          }}
        >
          {nomesDeFeriado.map((feriado) => (
            <li
              key={`${feriado.dia}-${feriado.nome}`}
              style={{
                fontSize: '5.2cqw',
                color: feriado.facultativo ? paleta.textoSuave : paleta.texto,
                lineHeight: 1.25,
              }}
            >
              {String(feriado.dia).padStart(2, '0')}/{String(mes).padStart(2, '0')} · {feriado.nome}
              {feriado.facultativo ? ' (facultativo)' : ''}
            </li>
          ))}
        </ul>
      ) : null}

      {semDatas ? (
        <span style={{ fontSize: '5.6cqw', color: paleta.textoSuave, textAlign: 'center' }}>
          Preencha mês e datas
        </span>
      ) : null}
    </div>
  );
}

/** Rótulo curto do mês, usado em grades do ano. */
export function rotuloCurtoMes(mes: number): string {
  return MESES_ABREV[mes - 1] ?? '';
}
